import { useMemo, useState } from 'react';
import { EventEmitter, useMount, useUnmount } from '@apia/util';
import { ApiaUtilTooltip } from './ApiaUtilTooltip';
import { AutomaticTooltip } from './AutomaticTooltip';
import { TTooltip, Tooltip } from './tooltip';
import { FreeFocusInside } from 'react-focus-lock';

export class ApiaUtilTooltips {
  #emitter = new EventEmitter<{
    changedList: ApiaUtilTooltip[];
  }>();
  tooltips: ApiaUtilTooltip[] = [];

  constructor() {
    document.addEventListener('keydown', (ev) => {
      if (
        ev.code === 'Escape' &&
        this.tooltips.find((c) => c.props.closeOnEscape !== false)
      ) {
        ev.stopImmediatePropagation();
        ev.preventDefault();

        this.tooltips = this.tooltips.filter(
          (current) => current.props.closeOnEscape === false,
        );
        this.#shoutChangedList();
      }
    });
  }

  #shoutChangedList = () => {
    this.#emitter.emit('changedList', [...this.tooltips]);
  };

  /**
   * Permite crear un tooltip que se abrirá automáticamente al estar parado
   * sobre un elemento durante 300ms
   */
  useHover = (tooltip?: Partial<TTooltip> & { timeout?: number }) => {
    let tt: AutomaticTooltip | undefined = undefined;

    tt = useMemo(() => {
      tt?.ref(null);
      return new AutomaticTooltip(tooltip ?? null, this);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tooltip]);

    useUnmount(() => {
      tt?.ref(null);
    });

    return tt;
  };

  close = (id: string) => {
    this.tooltips = this.tooltips.filter((current) => current.id !== id);
    this.#shoutChangedList();
  };

  closeAll = () => {
    this.tooltips = [];
    this.#shoutChangedList();
  };

  open = (tooltip: TTooltip) => {
    const controller = new ApiaUtilTooltip(tooltip, () => {
      this.close(controller.id);
      controller.props.onClose?.();
    });

    if (tooltip.closeOthers !== false) {
      this.tooltips = [controller];
    } else {
      this.tooltips = [...this.tooltips, controller];
    }
    this.#shoutChangedList();

    return controller;
  };

  Component = () => {
    const [tooltips, setTooltips] = useState<ApiaUtilTooltip[]>(this.tooltips);

    useMount(() => {
      const unsuscribeToList = this.#emitter.on('changedList', (ev) => {
        setTooltips(ev);
      });

      return () => {
        unsuscribeToList();
      };
    });

    return (
      <FreeFocusInside>
        {tooltips.map((current) => (
          <Tooltip
            {...current.props}
            onClose={() => {
              current.close();
            }}
            key={current.id}
          />
        ))}
      </FreeFocusInside>
    );
  };
}
